import { Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton, IconButtonProps } from '@fly/core';
import { copyStringToClipboard } from '@fly/shared';
import classNames from 'classnames';
import log from 'loglevel';

type FlyCopyButtonProps = {
  code: string;
  className?: string;
  size?: number;
  copyTimeoutMs?: number;
  onClick?: () => void;
  ref?: Ref<HTMLDivElement>;
} & Omit<IconButtonProps, 'size' | 'icon' | 'color' | 'variant' | 'onClick'>;

function FlyCopyButton({
  code,
  className,
  size = 14,
  copyTimeoutMs = 1500,
  onClick,
  ref,
  ...rest
}: FlyCopyButtonProps) {
  const { t } = useTranslation();
  const [icon, setIcon] = useState('copy');

  async function copyTextToClipboard(text: string) {
    try {
      await copyStringToClipboard(text);
      setIcon('check');
    } catch (err) {
      log.error('Failed to copy text: ', err);
    } finally {
      setTimeout(() => {
        setIcon('copy');
      }, copyTimeoutMs);
    }
  }

  return (
    <div className={classNames(className)} ref={ref}>
      <IconButton
        color='neutral'
        variant='ghost'
        size='1'
        aria-label={t('app.common.actions.copy_to_clipboard')}
        {...rest}
        onClick={() => {
          copyTextToClipboard(code);
          onClick?.();
        }}
        icon={icon}
        iconSize={size}
      />
    </div>
  );
}

export { FlyCopyButton };
